import { Component, HostListener, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DeckService } from 'src/app/registration/services/deck.service';

interface MenuOption {
  name: string;
  path: string;
  children?: MenuOption[];
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  gameplayOptions: MenuOption[] = [
    { name: 'Basic', path: '/gameplay-basic' },
    { name: 'Epic', path: '/gameplay-epic' },
    { name: 'Legendary', path: '/gameplay-legendary' },
    { name: 'Mythical', path: '/gameplay-mythical' }
  ];

  manualOptions: MenuOption[] = [
    { name: 'Introduction', path: '/manual' },
    { name: 'Game Setup', path: '/manual-next' },
    { 
      name: 'Abilities', 
      path: '', 
      children: [
        { name: 'Vision', path: '/manual-vision' },
        { name: 'Charisma', path: '/manual-charisma' },
        { name: 'Strength', path: '/manual-strength' },
        { name: 'Stamina', path: '/manual-stamina' },
        { name: 'Speed', path: '/manual-speed' },
        { name: 'Empathy', path: '/manual-empathy' },
        { name: 'Intelligence', path: '/manual-intelligence' },
        { name: 'Willpower', path: '/manual-willpower' },
        { name: 'Luck', path: '/manual-luck' }
      ]
    },
    { name: 'Special Action', path: '/manual-special-actions' },
    { name: 'Kodigo', path: '/kodigo' }
  ];

  registrationOptions: MenuOption[] = [
    { name: 'User Registration', path: '/registration/user' },
    { name: 'Deck Registration', path: '/registration/deck' }
  ];

  activeTab: string = '';
  activeSection: string = '';
  isMenuOpen = false;
  openSubmenu: string | null = null;
  hoverTimeout: any;
  showGameplayDropdown = false;
  showManualDropdown = false;
  showRegistrationDropdown = false;
  showAbilities = false;
  showAbilitiesMobile = false;
  isMobileView = false;
  isScrolled = false;
  isIntroductionPage = false;

  constructor(
    private router: Router,
    public deckService: DeckService
  ) {}

  ngOnInit(): void {
    this.checkViewport();
    this.onWindowScroll();
    
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateActiveTab();
    });
    
    this.updateActiveTab();
  }

  private updateActiveTab(): void {
    const url = this.router.url;
    
    // this.isIntroductionPage = url.includes('/home');
    // this.isIntroductionPage = url.includes('/introduction');
    this.activeSection = ''; // Reset active section
    
    if (url.includes('/intorduction') || url === '/') {
    // if (url.includes('/home') || url === '/') {
      this.activeTab = 'home';
    } else if (url.includes('/introduction')) {
      this.activeTab = 'introduction';
    } else if (url.includes('/cards')) {
      this.activeTab = 'cards';
    } else if (url.includes('/ranking')) {
      this.activeTab = 'ranking';
    } else if (url.includes('/statistics')) {
      this.activeTab = 'statistics';
    } else if (url.includes('/tournaments')) {
      this.activeTab = 'tournaments';
    } else if (url.includes('/contactus')) {
      this.activeTab = 'contactus';
    } else if (url.includes('/registration')) {
      this.activeTab = 'registration';
    } else if (this.gameplayOptions.some(option => url.includes(option.path))) {
      this.activeTab = 'gameplay';
    } else if (this.manualOptions.some(option => 
      option.children ? 
        option.children.some(child => url.includes(child.path)) : 
        url.includes(option.path))) {
      this.activeTab = 'manual';
    }
  }

  scrollToSection(sectionId: string): void {
    this.isMenuOpen = false; 
    
    this.activeSection = sectionId;
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else if (sectionId === 'footer') {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkViewport();
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.isScrolled = scrollPosition > 10;
  }

  checkViewport(): void {
    this.isMobileView = window.innerWidth < 992;
    if (!this.isMobileView) {
      this.isMenuOpen = false;
      this.openSubmenu = null;
    }
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('.mobile-nav') && 
        !(event.target as HTMLElement).closest('.hamburger')) {
      this.isMenuOpen = false;
      this.openSubmenu = null;
    }
  }

  onMouseEnterGameplay(): void {
    if (this.isMobileView) return;
    clearTimeout(this.hoverTimeout);
    this.showGameplayDropdown = true;
  }

  onMouseLeaveGameplay(): void {
    if (this.isMobileView) return;
    this.hoverTimeout = setTimeout(() => {
      this.showGameplayDropdown = false;
    }, 300);
  }

  isGameplayActive(): boolean {
    return this.gameplayOptions.some(option => 
      this.router.url.includes(option.path));
  }

  navigateToFirstGameplay(): void {
    if (this.gameplayOptions.length > 0) {
      this.navigateTo(this.gameplayOptions[0].path);
    }
  }

  toggleGameplayDropdown(event: Event): void {
    event.preventDefault();
    this.setActiveTab('gameplay');
    this.showGameplayDropdown = !this.showGameplayDropdown;
    if (this.showGameplayDropdown && this.isMobileView) {
      this.navigateToFirstGameplay();
    }
  }

  onMouseEnterManual(): void {
    if (this.isMobileView) return;
    clearTimeout(this.hoverTimeout);
    this.showManualDropdown = true;
  }

  onMouseLeaveManual(): void {
    if (this.isMobileView) return;
    this.hoverTimeout = setTimeout(() => {
      this.showManualDropdown = false;
      this.showAbilities = false;
    }, 300);
  }

  isManualActive(): boolean {
    return this.manualOptions.some(option => {
      if (option.children) {
        return option.children.some(child =>
          this.router.url.includes(child.path));
      }
      return this.router.url.includes(option.path);
    });
  }

  navigateToFirstManual(): void {
    if (this.manualOptions.length > 0) {
      this.navigateTo(this.manualOptions[0].path);
    }
  }

  toggleManualDropdown(event: Event): void {
    event.preventDefault();
    this.setActiveTab('manual');
    this.showManualDropdown = !this.showManualDropdown;
    if (this.showManualDropdown && this.isMobileView) {
      this.navigateToFirstManual();
    }
  }

  onMouseEnterRegistration(): void {
    if (this.isMobileView) return;
    clearTimeout(this.hoverTimeout);
    this.showRegistrationDropdown = true;
  }

  onMouseLeaveRegistration(): void {
    if (this.isMobileView) return;
    this.hoverTimeout = setTimeout(() => {
      this.showRegistrationDropdown = false;
    }, 300);
  }

  toggleRegistrationDropdown(event: Event): void {
    event.preventDefault();
    this.setActiveTab('registration');
    this.showRegistrationDropdown = !this.showRegistrationDropdown;
  }

  navigateTo(path: string): void {
    this.setActiveTab(path);
    this.showManualDropdown = false;
    this.showAbilities = false;
    this.showGameplayDropdown = false;
    this.isMenuOpen = false;
    this.router.navigate([path]).then(() => {
      window.scrollTo(0, 0);
    });
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    if (!this.isMenuOpen) {
      this.openSubmenu = null;
      this.showGameplayDropdown = false;
      this.showManualDropdown = false;
      this.showAbilities = false;
      this.showAbilitiesMobile = false;
    }
  }

  toggleSubmenu(name: string): void {
    if (this.openSubmenu === name) {
      this.openSubmenu = null;
      this.showAbilitiesMobile = false;
    } else {
      this.openSubmenu = name;
      if (name === 'manual') {
        this.navigateToFirstManual();
      } else if (name === 'gameplay') {
        this.navigateToFirstGameplay();
      }
    }
  }

  setActiveTab(tabName: string): void {
    this.activeTab = tabName;
    if (this.isMobileView) {
      this.isMenuOpen = false;
      this.openSubmenu = null;
    }
  }

  getAbilitiesOptions(): MenuOption[] {
    const abilitiesOption = this.manualOptions.find(option => option.name === 'Abilities');
    return abilitiesOption?.children || [];
  }

  logout(): void {
    this.deckService.logout();
    this.navigateTo("/registration/deck/login");
  }

  isActiveRoute(path: string): boolean {
    return this.router.url.includes(path);
  }
}